import { useCallback, useState } from 'react';

import { useConversationContext } from '@/contexts/v2/ConversationContext';

import { ConversationErrorNotice } from './ConversationErrorNotice';

export interface WiredConversationErrorNoticeProps {
  className?: string;
}

/**
 * Container that surfaces {@link ConversationErrorNotice} from live conversation
 * state (ANA-194). Renders the inline error + Retry whenever a turn has failed
 * (`phase === 'error'` with an `error`) — EXCEPT when voice mode is already
 * surfacing that failure through its own in-bar chrome (`voiceState === 'error'`),
 * which would be a duplicate surface.
 *
 * Critically it is NOT gated to text-only: a lingering text-turn error must stay
 * retryable even if the user switches to voice before retrying (voice idle/
 * listening ≠ a voice error), otherwise the failed message is orphaned with no
 * recovery affordance until they switch back to text (Bugbot a56570b9).
 *
 * Shared by all three shell modes (peek + detailed render it in
 * `<ConversationShell>`; focused renders it in `<ChatThread>`) so the behaviour
 * is identical everywhere.
 */
export function WiredConversationErrorNotice({
  className,
}: WiredConversationErrorNoticeProps) {
  const { phase, error, retry, inputMode, voiceState } = useConversationContext();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = useCallback(() => {
    setRetrying(true);
    // `retry()` synchronously clears `error` (→ phase leaves 'error'), so this
    // notice usually unmounts before the retry resolves; the flag is a guard for
    // the brief window and any double-click.
    void Promise.resolve(retry()).finally(() => setRetrying(false));
  }, [retry]);

  // Suppress only when voice is actively showing its OWN error chrome — not for
  // all of voice mode — so a text failure carried into voice stays retryable.
  const voiceShowsOwnError = inputMode === 'voice' && voiceState === 'error';
  if (phase !== 'error' || !error || voiceShowsOwnError) return null;

  return (
    <ConversationErrorNotice
      error={error}
      onRetry={handleRetry}
      retrying={retrying}
      className={className}
    />
  );
}
